/**
 * Onboarding Wizard - Complete Setup Flow
 * Multi-step wizard to collect all necessary user and company information
 * Minimalist, modern design matching Lexy's aesthetic
 */
import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, Building2, Briefcase, FileSignature, Mail, Sparkles,
  ChevronRight, ChevronLeft, Check, Camera, Loader2, Upload,
  Phone, MapPin, Hash, Clock, DollarSign, Scale, Globe
} from 'lucide-react';
import axios from 'axios';
import { auth } from '../../utils/auth';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import OutlookIntegration from '../integrations/OutlookIntegration';
import GoogleIntegration from '../integrations/GoogleIntegration';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface OnboardingWizardProps {
  user: any;
  onComplete: () => void;
  onSkip?: () => void;
}

const PRACTICE_AREAS = [
  'Strafferett',
  'Familierett',
  'Arbeidsrett',
  'Forretningsjus',
  'Eiendomsrett',
  'Immaterialrett',
  'Skatterett',
  'Konkurs og restrukturering',
  'Personskade',
  'Innvandring',
  'Annet'
];

const ROLES = [
  'Partner',
  'Advokat',
  'Advokatfullmektig',
  'Dommerfullmektig',
  'Juridisk rådgiver',
  'Paralegal',
  'Annet'
];

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ user, onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const signatureRef = useRef<HTMLDivElement>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    // Personal
    name: user?.name || '',
    phone: user?.phone || '',
    profilePicture: user?.profile_picture_url || '',
    
    // Company
    companyName: '',
    companyLogo: '',
    companyAddress: '',
    organizationNumber: '',
    
    // Professional
    role: '',
    practiceAreas: [] as string[],
    hourlyRate: 1500,
    timeIncrement: 0.25,
    
    // Signature
    emailSignature: '',
    
    // Preferences
    language: 'no'
  });

  const [previewUrl, setPreviewUrl] = useState(user?.profile_picture_url || '');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const steps = [
    { id: 'welcome', title: 'Velkommen', icon: Sparkles },
    { id: 'personal', title: 'Personlig info', icon: User },
    { id: 'company', title: 'Firma', icon: Building2 },
    { id: 'professional', title: 'Profesjonell', icon: Briefcase },
    { id: 'signature', title: 'E-postsignatur', icon: FileSignature },
    { id: 'integrations', title: 'Integrasjoner', icon: Mail },
    { id: 'complete', title: 'Ferdig', icon: Check }
  ];

  const updateFormData = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Filen er for stor. Maks 5MB.');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = auth.getToken();
      const response = await axios.post(`${API}/auth/profile-picture`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const newUrl = response.data.profile_picture_url;
      setPreviewUrl(newUrl.startsWith('http') ? newUrl : `${process.env.REACT_APP_BACKEND_URL}${newUrl}`);
      updateFormData('profilePicture', newUrl);
      toast.success('Profilbilde lastet opp');
    } catch (error) {
      toast.error('Kunne ikke laste opp bilde');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleNext = async () => {
    // Save data at certain steps
    if (currentStep === 1) { // After personal info
      await savePersonalInfo();
    } else if (currentStep === 2) { // After company info
      await saveCompanyInfo();
    } else if (currentStep === 3) { // After professional info
      await saveProfessionalInfo();
    } else if (currentStep === 4) { // After signature
      await saveSignature();
    }
    
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const savePersonalInfo = async () => {
    try {
      const token = auth.getToken();
      await axios.patch(`${API}/users/me`, {
        name: formData.name,
        phone: formData.phone
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error saving personal info:', error);
    }
  };

  const saveCompanyInfo = async () => {
    try {
      const token = auth.getToken();
      await axios.patch(`${API}/admin/tenant`, {
        company_name: formData.companyName,
        settings: {
          address: formData.companyAddress,
          organization_number: formData.organizationNumber
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error saving company info:', error);
    }
  };

  const saveProfessionalInfo = async () => {
    try {
      const token = auth.getToken();
      await axios.patch(`${API}/users/me/settings`, {
        role: formData.role,
        practice_areas: formData.practiceAreas,
        default_hourly_rate: formData.hourlyRate,
        min_time_increment: formData.timeIncrement
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error saving professional info:', error);
    }
  };

  const saveSignature = async () => {
    try {
      const token = auth.getToken();
      await axios.post(`${API}/settings/email-signature`, {
        signature: formData.emailSignature
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error saving signature:', error);
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      const token = auth.getToken();
      await axios.patch(`${API}/auth/me/onboarding`, {
        onboarding_completed: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Celebration!
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
      
      setTimeout(() => {
        onComplete();
      }, 1500);
    } catch (error) {
      toast.error('Kunne ikke fullføre oppsett');
      setSaving(false);
    }
  };

  const togglePracticeArea = (area: string) => {
    setFormData(prev => ({
      ...prev,
      practiceAreas: prev.practiceAreas.includes(area)
        ? prev.practiceAreas.filter(a => a !== area)
        : [...prev.practiceAreas, area]
    }));
  };

  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case 0: // Welcome
        return (
          <div className="text-center py-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-20 h-20 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6"
            >
              <Sparkles className="w-10 h-10 text-white" />
            </motion.div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Velkommen til Lexy, {user?.name?.split(' ')[0] || 'deg'}!
            </h2>
            <p className="text-gray-500 mb-8 max-w-md mx-auto">
              La oss sette opp kontoen din på under 2 minutter, slik at Lexy kan gi deg den beste opplevelsen.
            </p>
            <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto">
              {[
                { icon: User, label: 'Din profil' },
                { icon: Building2, label: 'Firma-info' },
                { icon: Mail, label: 'E-post' }
              ].map((item, idx) => (
                <div key={idx} className="p-3 rounded-lg bg-gray-50 text-center">
                  <item.icon className="w-5 h-5 text-violet-500 mx-auto mb-1" />
                  <span className="text-xs text-gray-600">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 1: // Personal Info
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Personlig informasjon</h2>
              <p className="text-sm text-gray-500">Fortell oss litt om deg selv</p>
            </div>

            {/* Profile Photo */}
            <div className="flex justify-center">
              <div className="relative group">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Profile"
                    className="w-24 h-24 rounded-full object-cover ring-4 ring-gray-100"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-violet-100 to-violet-50 flex items-center justify-center ring-4 ring-gray-100">
                    <span className="text-3xl font-semibold text-violet-500">
                      {formData.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                )}
                <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                  {uploadingPhoto ? (
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  ) : (
                    <Camera className="w-6 h-6 text-white" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    disabled={uploadingPhoto}
                  />
                </label>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Fullt navn *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => updateFormData('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  placeholder="Ola Nordmann"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Telefon
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => updateFormData('phone', e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="+47 XXX XX XXX"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 2: // Company Info
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Firmainformasjon</h2>
              <p className="text-sm text-gray-500">Informasjon om ditt advokatfirma</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Firmanavn *
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => updateFormData('companyName', e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="Advokatfirmaet AS"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Organisasjonsnummer
                </label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={formData.organizationNumber}
                    onChange={(e) => updateFormData('organizationNumber', e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="123 456 789"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Adresse
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <textarea
                    value={formData.companyAddress}
                    onChange={(e) => updateFormData('companyAddress', e.target.value)}
                    rows={2}
                    className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
                    placeholder="Storgata 1, 0123 Oslo"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 3: // Professional Info
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Profesjonell profil</h2>
              <p className="text-sm text-gray-500">Din rolle og praksis</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Stilling
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => updateFormData('role', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                >
                  <option value="">Velg stilling</option>
                  {ROLES.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prakisområder
                </label>
                <div className="flex flex-wrap gap-2">
                  {PRACTICE_AREAS.map(area => (
                    <button
                      key={area}
                      type="button"
                      onClick={() => togglePracticeArea(area)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                        formData.practiceAreas.includes(area)
                          ? 'bg-violet-100 text-violet-700 border-2 border-violet-300'
                          : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                      }`}
                    >
                      {area}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Standard timepris (NOK)
                  </label>
                  <input
                    type="number"
                    value={formData.hourlyRate}
                    onChange={(e) => updateFormData('hourlyRate', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Tidsintervall
                  </label>
                  <select
                    value={formData.timeIncrement}
                    onChange={(e) => updateFormData('timeIncrement', parseFloat(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  >
                    <option value={0.1}>6 min</option>
                    <option value={0.25}>15 min</option>
                    <option value={0.5}>30 min</option>
                    <option value={1}>60 min</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        );

      case 4: // Email Signature
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">E-postsignatur</h2>
              <p className="text-sm text-gray-500">Lim inn eller skriv din e-postsignatur</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lim inn fra Outlook eller skriv manuelt
              </label>
              <div
                ref={signatureRef}
                contentEditable
                onInput={(e) => updateFormData('emailSignature', e.currentTarget.innerHTML)}
                onPaste={(e) => {
                  setTimeout(() => {
                    if (e.currentTarget) {
                      updateFormData('emailSignature', e.currentTarget.innerHTML);
                    }
                  }, 0);
                }}
                className="w-full min-h-[120px] p-4 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                style={{ outline: 'none' }}
                suppressContentEditableWarning={true}
              />
              <p className="text-xs text-gray-400 mt-2">
                Tip: Kopier signaturen fra Outlook og lim inn. Formateringen beholdes.
              </p>
            </div>

            {/* Quick templates */}
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">Eller bruk en mal:</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const html = `<p>Med vennlig hilsen,</p><p><strong>${formData.name || 'Ditt navn'}</strong></p><p>${formData.role || 'Advokat'}</p>`;
                    updateFormData('emailSignature', html);
                    if (signatureRef.current) signatureRef.current.innerHTML = html;
                  }}
                  className="px-3 py-1.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
                >
                  Enkel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const html = `<p>Med vennlig hilsen,</p><p><strong>${formData.name || 'Ditt navn'}</strong></p><p>${formData.role || 'Advokat'}</p><p style="color: #666; font-size: 12px;">${formData.companyName || 'Advokatfirma'}</p><p style="color: #666; font-size: 12px;">Tlf: ${formData.phone || '+47 XXX XX XXX'}</p>`;
                    updateFormData('emailSignature', html);
                    if (signatureRef.current) signatureRef.current.innerHTML = html;
                  }}
                  className="px-3 py-1.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
                >
                  Med kontaktinfo
                </button>
              </div>
            </div>
          </div>
        );

      case 5: // Integrations
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Koble til e-post og kalender</h2>
              <p className="text-sm text-gray-500">Synkroniser for å få full oversikt i Lexy</p>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <OutlookIntegration />
              <GoogleIntegration />
            </div>

            <p className="text-xs text-center text-gray-400">
              Du kan også koble til senere fra innstillinger
            </p>
          </div>
        );

      case 6: // Complete
        return (
          <div className="text-center py-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <Check className="w-10 h-10 text-white" />
            </motion.div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Alt er klart!
            </h2>
            <p className="text-gray-500 mb-8 max-w-md mx-auto">
              Lexy er nå satt opp og klar til bruk. Du kan når som helst endre innstillingene dine.
            </p>
            
            <div className="bg-gray-50 rounded-xl p-4 max-w-sm mx-auto text-left space-y-2">
              {formData.name && (
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-emerald-500" />
                  <span className="text-gray-600">Profil: {formData.name}</span>
                </div>
              )}
              {formData.companyName && (
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-emerald-500" />
                  <span className="text-gray-600">Firma: {formData.companyName}</span>
                </div>
              )}
              {formData.role && (
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-emerald-500" />
                  <span className="text-gray-600">Stilling: {formData.role}</span>
                </div>
              )}
              {formData.emailSignature && (
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-emerald-500" />
                  <span className="text-gray-600">E-postsignatur konfigurert</span>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
      >
        {/* Progress Bar */}
        <div className="h-1 bg-gray-100">
          <motion.div
            className="h-full bg-gradient-to-r from-violet-500 to-purple-600"
            initial={{ width: 0 }}
            animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>

        {/* Step Indicator */}
        <div className="flex justify-center gap-2 pt-6 pb-2">
          {steps.map((step, idx) => (
            <div
              key={step.id}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx === currentStep
                  ? 'bg-violet-500'
                  : idx < currentStep
                  ? 'bg-violet-200'
                  : 'bg-gray-200'
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="px-6 pb-6 min-h-[400px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
          <div>
            {currentStep > 0 && currentStep < steps.length - 1 && (
              <button
                onClick={handleBack}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
              >
                <ChevronLeft className="w-4 h-4" />
                Tilbake
              </button>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            {currentStep > 0 && currentStep < steps.length - 1 && (
              <button
                onClick={() => setCurrentStep(steps.length - 1)}
                className="text-sm text-gray-400 hover:text-gray-600"
              >
                Hopp over
              </button>
            )}
            
            {currentStep < steps.length - 1 ? (
              <button
                onClick={handleNext}
                className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
              >
                {currentStep === 0 ? 'Kom i gang' : 'Neste'}
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleComplete}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-lg text-sm font-medium hover:from-violet-600 hover:to-purple-700 transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                Start Lexy
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default OnboardingWizard;
